import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useEditData } from "../../../hooks/useEditData";
import FormLayout from '../../Generic/FormLayout';
import FormInput from '../../Generic/FormInput';
import FormCheckbox from '../../Generic/FormCheckbox';
import { useSetMenu } from "../../../hooks/useSetMenu";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import * as yup from "yup";
import FormSelect from '../../Generic/FormSelect';
import { useSnackbar } from "notistack";
import { Button, Modal, Tab, Tabs, Badge } from 'react-bootstrap';

const Edit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { mainStore } = useStore();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    useSetMenu(mainStore.CriteriaValueMenuType, mainStore.CriteriaValueMenuNo);
    
    // State for batch mode
    const [showBatchMode, setShowBatchMode] = useState(false);
    const [batchValues, setBatchValues] = useState("");
    const [parsedValues, setParsedValues] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentValue, setCurrentValue] = useState("");
    const [batchTab, setBatchTab] = useState("update");
    
    const schema = yup.object({
        value: yup
            .string()
            .required(t('requiredField'))
            .max(250, t('maxLength', { max: 250 })),
        subcategoryId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        criteriaId: yup.string().required(t("selectRequired")),
    });

    const { register, handleSubmit, formState: { errors }, setValue, getValues } = useForm({ resolver: yupResolver(schema) });
    
    const onSubmit = data => {
        if (showBatchMode) {
            handleBatchSubmit();
        } else {
            updateData(data);
        }
    };

    // Process the batch input into array of values
    useEffect(() => {
        if (batchValues.trim()) {
            // Split by newlines, commas, or semicolons
            const values = batchValues
                .split(/[\n,;]/)
                .map(val => val.trim())
                .filter(val => val.length > 0 && val.length <= 250);
            
            setParsedValues(values);
        } else {
            setParsedValues([]);
        }
    }, [batchValues]);

    const [panels, setPanels] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [criterias, setCriterias] = useState([]);
    const [selectedCriteria, setSelectedCriteria] = useState(null);
    const [originalCriteriaValueId, setOriginalCriteriaValueId] = useState(null);

    const additionalDataFetch = async (criteriaValueData) => {
        try {
            // Store the current value for batch mode reference
            setCurrentValue(criteriaValueData.value);
            setOriginalCriteriaValueId(criteriaValueData.id);
            
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
            const selPanel = panelsResponse.data.find(
                (c) => c.value === criteriaValueData.panelId.toString()
            );
            setSelectedPanel(selPanel);    

            let categoriesResponse;
            if (selPanel) {
                categoriesResponse = await axiosPrivate.get(
                    `/Categories/SelectOptionsByPanel/${selPanel.value}`
                );
                setCategories(categoriesResponse.data);
            }
            const selCategory = categoriesResponse.data.find(
                (c) => c.value === criteriaValueData.categoryId.toString()
            );
            setSelectedCategory(selCategory);

            let subcategoriesResponse;
            if (selCategory) {
                subcategoriesResponse = await axiosPrivate.get(
                    `/Subcategories/SelectOptionsByCategory/${selCategory.value}`
                );
                setSubcategories(subcategoriesResponse.data);
            }
            const selSubcategory = subcategoriesResponse.data.find(
                (c) => c.value === criteriaValueData.subcategoryId.toString()
            );
            setSelectedSubcategory(selSubcategory);

            let criteriasResponse;
            if (selSubcategory) {
                criteriasResponse = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selSubcategory.value}`);
                setCriterias(criteriasResponse.data);
            }
            const selected = criteriasResponse.data.find(c => c.value === criteriaValueData.criteriaId.toString());
            setSelectedCriteria(selected);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    // Function to handle batch updates
    const handleBatchSubmit = async () => {
        if (parsedValues.length === 0) {
            enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
            return;
        }
        
        try {
            setIsSubmitting(true);
            
            if (batchTab === "update") {
                // Update the existing value
                const updateData = {
                    id: originalCriteriaValueId,
                    criteriaId: parseInt(getValues("criteriaId"), 10),
                    panelId: parseInt(getValues("panelId"), 10),
                    categoryId: parseInt(getValues("categoryId"), 10),
                    subcategoryId: parseInt(getValues("subcategoryId"), 10),
                    value: getValues("value"),
                    isActive: getValues("isActive") || false
                };
                
                await axiosPrivate.put(`/CriteriaValues/${originalCriteriaValueId}`, updateData);
                
                // Then add additional values for the same criteria
                if (parsedValues.length > 0) {
                    const batchRequest = {
                        criteriaId: parseInt(getValues("criteriaId"), 10),
                        panelId: parseInt(getValues("panelId"), 10),
                        categoryId: parseInt(getValues("categoryId"), 10),
                        subcategoryId: parseInt(getValues("subcategoryId"), 10),
                        values: parsedValues
                    };
                    
                    await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
                }
                
                enqueueSnackbar(t("batchUpdateSuccess"), { variant: "success" });
            } else if (batchTab === "replace") {
                // Delete the existing value (soft delete)
                await axiosPrivate.delete(`/CriteriaValues/${originalCriteriaValueId}`);
                
                // Create all new values including the original + new values
                const allValues = [getValues("value"), ...parsedValues];
                
                const batchRequest = {
                    criteriaId: parseInt(getValues("criteriaId"), 10),
                    panelId: parseInt(getValues("panelId"), 10),
                    categoryId: parseInt(getValues("categoryId"), 10),
                    subcategoryId: parseInt(getValues("subcategoryId"), 10),
                    values: allValues
                };
                
                await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
                enqueueSnackbar(t("batchUpdateSuccess"), { variant: "success" });
            }
            
            navigate("/CriteriaValue/List");
        } catch (error) {
            enqueueSnackbar(t("batchUpdateError"), { variant: "error" });
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCriteriaChange = selectedOption => {
        setValue("criteriaId", selectedOption.value, { shouldValidate: true });
        setSelectedCriteria(selectedOption);
    };
    const handleSubcategoryChange = async selectedOption => {
        setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCriterias([]);
            } else {
                setCriterias(response.data);
            }
            setSelectedCriteria(null);
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }
            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setCriterias([]);

            setValue("subcategoryId", null, { shouldValidate: true });
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSubcategories([]);
            setCriterias([]);

            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };

    const { notFound, updateData } = useEditData(axiosPrivate, "/CriteriaValues", id, setValue, navigate, additionalDataFetch);

    const goToList = () => {
        navigate("/CriteriaValue/List");
    };
    
    // Toggle batch mode
    const toggleBatchMode = () => {
        setShowBatchMode(!showBatchMode);
        if (!showBatchMode) {
            // Initialize the batch values with the current value
            setBatchValues("");
        }
    };

    if (notFound) {
        return <div className="not-found-myDiv">
            <div className="not-found-container">
                <div className="not-found-content">
                    <h1>{t("pageNotFoundTitle")}</h1>
                    <p>{t("noDataWithIdMessage")}</p>
                    <button onClick={goToList} className="btn btn-primary">
                        {t("goTo") + " " + t("list") + " " + t("criteriaValues")}
                    </button>
                </div>
            </div>
        </div>;
    }

    return (
        <FormLayout title={t("criteriaValueEditTitle")} onSubmit={handleSubmit(onSubmit)} onCancel={() => navigate(-1)}>
            <FormSelect label="panel" name="panelId" register={register} value={selectedPanel} errors={errors} options={panels} onChange={handlePanelChange} />
            <FormSelect label="category" name="categoryId" register={register} errors={errors} options={categories} value={selectedCategory} onChange={handleCategoryChange} />
            <FormSelect label="subcategory" name="subcategoryId" options={subcategories} value={selectedSubcategory} onChange={handleSubcategoryChange} register={register} errors={errors} />
            <FormSelect label="criteria" name="criteriaId" options={criterias} value={selectedCriteria} onChange={handleCriteriaChange} register={register} errors={errors} />
            
            {/* Toggle button for batch mode */}
            <div className="col-md-12 mb-3">
                <Button 
                    variant={showBatchMode ? "primary" : "outline-secondary"}
                    onClick={toggleBatchMode}
                    className="mb-3"
                >
                    <i className="fas fa-layer-group me-1"></i> {t("batchModeInEdit")}
                </Button>
            </div>

            {!showBatchMode ? (
                // Single value edit mode
                <>
                    <FormInput label="criteriaValue" name="value" register={register} errors={errors} />
                    <FormCheckbox label="isActive" register={register} name="isActive" colSize={4} />
                </>
            ) : (
                // Batch mode
                <div className="col-md-12 mb-4">
                    <Tabs
                        activeKey={batchTab}
                        onSelect={(key) => setBatchTab(key)}
                        id="batch-tabs"
                        className="mb-3"
                    >
                        <Tab eventKey="update" title={t("updateExistingValues")}>
                            <div className="p-3 border-start border-end border-bottom mb-3">
                                <div className="alert alert-info">
                                    <strong>{t("currentValueText")}:</strong> {currentValue}
                                </div>
                                
                                <FormInput label="criteriaValue" name="value" register={register} errors={errors} />
                                <FormCheckbox label="isActive" register={register} name="isActive" colSize={4} />
                                
                                <div className="mt-3">
                                    <label className="form-label">{t("batchValues")}</label>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        placeholder={t("enterMultipleValues")}
                                        value={batchValues}
                                        onChange={(e) => setBatchValues(e.target.value)}
                                    />
                                    <div className="form-text">
                                        {t("batchValuesHelp")}
                                    </div>
                                </div>
                                
                                {parsedValues.length > 0 && (
                                    <div className="mt-3">
                                        <h6>{t("valuePreview")} ({parsedValues.length})</h6>
                                        <div className="border p-2 bg-light">
                                            {parsedValues.map((val, index) => (
                                                <Badge bg="secondary" className="me-1 mb-1" key={index}>{val}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Tab>
                        <Tab eventKey="replace" title={t("deleteAndCreateNew")}>
                            <div className="p-3 border-start border-end border-bottom mb-3">
                                <div className="alert alert-warning">
                                    <strong>{t("currentValueText")}:</strong> {currentValue}
                                    <p className="mb-0 mt-2">
                                        <small>
                                            {t("deleteAndCreateNew")}
                                        </small>
                                    </p>
                                </div>
                                
                                <FormInput label="criteriaValue" name="value" register={register} errors={errors} />
                                
                                <div className="mt-3">
                                    <label className="form-label">{t("batchValues")}</label>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        placeholder={t("enterMultipleValues")}
                                        value={batchValues}
                                        onChange={(e) => setBatchValues(e.target.value)}
                                    />
                                    <div className="form-text">
                                        {t("batchValuesHelp")}
                                    </div>
                                </div>
                                
                                {parsedValues.length > 0 && (
                                    <div className="mt-3">
                                        <h6>{t("valuePreview")} ({parsedValues.length})</h6>
                                        <div className="border p-2 bg-light">
                                            {parsedValues.map((val, index) => (
                                                <Badge bg="secondary" className="me-1 mb-1" key={index}>{val}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            )}
        </FormLayout>
    );
};

export default observer(Edit);